import React, { useCallback, useState } from 'react';
import { VaultRequest } from '../../api/vault';
import * as S from './styles';

interface Props {
	requests: VaultRequest[];
	selectedId: string | null;
	initialFilter: string;
	onSelect: (request: VaultRequest) => void;
	onFilterChange: (filter: string) => void;
}

const formatTime = (value: unknown) => {
	if (!value) return '';
	const d = new Date(String(value));
	if (isNaN(d.getTime())) return String(value);
	const hh = String(d.getHours()).padStart(2, '0');
	const mm = String(d.getMinutes()).padStart(2, '0');
	const ss = String(d.getSeconds()).padStart(2, '0');
	const ms = String(d.getMilliseconds()).padStart(3, '0');
	return `${hh}:${mm}:${ss}.${ms}`;
};

// Map request methods to HTTP verbs for the badge
const getHttpMethod = (method: string): string => {
	if (method.startsWith('get') || method === 'healthcheck') return 'GET';
	return 'POST';
};

const RequestList = ({ requests, selectedId, initialFilter, onSelect, onFilterChange }: Props) => {
	//--- HOOKS
	const [filter, setFilter] = useState(initialFilter);

	//--- HANDLERS
	const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setFilter(e.target.value);
	}, []);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				onFilterChange(filter);
			}
		},
		[filter, onFilterChange]
	);

	//--- RENDERING
	return (
		<>
			<S.FilterBar>
				<S.FilterInput
					placeholder="Filter by UUID, user, method..."
					value={filter}
					onChange={onInputChange}
					onKeyDown={onKeyDown}
				/>
			</S.FilterBar>
			<S.PanelBody>
				{requests.map((req, i) => (
					<React.Fragment key={req.id}>
						{i > 0 && <S.RequestDivider />}
						<S.RequestCard
							$selected={req.id === selectedId}
							onClick={() => onSelect(req)}
						>
							<S.MethodBadge $method={getHttpMethod(req.method)}>
								{getHttpMethod(req.method)}
							</S.MethodBadge>
							<S.RequestPath>{req.method}</S.RequestPath>
							<S.RequestTime>{formatTime(req.createdAt)}</S.RequestTime>
						</S.RequestCard>
					</React.Fragment>
				))}
				{requests.length === 0 && <S.EmptyState>No requests found</S.EmptyState>}
			</S.PanelBody>
		</>
	);
};

export default RequestList;
